import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";

const WP_ENDPOINT = "wp-json/smassets/v1/site-users";

export interface CreateSiteUserPayload {
  user_email: string;
  first_name?: string;
  last_name?: string;
  student_id?: string;
  role?: string;
  grade?: number | null;
  homeroom?: string | null;
  enrollment_status?: string;
  user_login?: string;
  password?: string;
}

export const useCreateSiteUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const createSiteUser = useCallback(
    async (payload: CreateSiteUserPayload): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(WP_ENDPOINT);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const msg = body?.message ?? `Failed to create user (${response.status})`;
          setError(msg);
          setLoading(false);
          return false;
        }

        setLoading(false);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to create user";
        setError(errorMessage);
        setLoading(false);
        return false;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  const clearError = () => setError(null);

  return { createSiteUser, loading, error, clearError };
};
